/**
 * Human Review Queue Service (US-13-GR-07)
 *
 * Ported from the consumer portal on 2026-09-08 by David's ratified decision on
 * Trello card yvw6hcA2: the review queue is an admin surface, so it lives in
 * ChellessAdmin and the portal copy is deleted.
 *
 * Admin drain path for the Easy Button "queued for human review" fallback. When
 * an Easy Button generation times out or fails, EasyButtonService.AddAsync parks
 * an AITaskQueue row at Status=AwaitingHumanReview. These calls let a platform
 * admin list, complete, and escalate that queue so the 24h SLA is meetable.
 *
 * All endpoints are [Authorize(Roles = "Admin")] on AI.API; a non-admin caller
 * gets a 403 which the UI surfaces as "admins only".
 *
 * Base URL: the admin SWA builds with VITE_API_URL pointed at TaskManager.API
 * directly, not the gateway, so an AI.API route needs its own host. Set
 * VITE_AI_API_URL to the AI.API host; leave it unset in local dev to use the
 * Vite /api proxy. Same pattern as adminProfileClient.ts (Profile.API) and
 * adminModerationService.ts (Journals.API).
 */
import { authenticatedFetch } from '../api/authenticatedFetch';
import { COLDSTART_RETRY_CONFIG } from '../api/resilientFetch';

const AI_BASE: string = import.meta.env.VITE_AI_API_URL || '';
const BASE = `${AI_BASE}/api/ai/human-review`;

/** Priority levels, matching AI.Model AITaskPriority. AI.API sends them by name. */
export type AITaskPriority = 'Low' | 'Normal' | 'High' | 'Critical';

export const AI_TASK_PRIORITY_VALUE: Record<AITaskPriority, number> = {
  Low: 0,
  Normal: 1,
  High: 2,
  Critical: 3,
};

/** Task statuses, matching AI.Model AITaskStatus. AI.API sends them by name. */
export type AITaskStatus =
  | 'Queued'
  | 'InProgress'
  | 'Completed'
  | 'Failed'
  | 'AwaitingHumanReview'
  | 'Cancelled';

const AI_TASK_PRIORITY_NAMES: AITaskPriority[] = ['Low', 'Normal', 'High', 'Critical'];

/**
 * Normalizes a wire priority to its name. The API sends names (Low..Critical);
 * 0-3 is still accepted. Anything unrecognised falls back to 'Normal'.
 */
export function toAITaskPriority(value: AITaskPriority | number | string | null | undefined): AITaskPriority {
  if (typeof value === 'number') return AI_TASK_PRIORITY_NAMES[value] ?? 'Normal';
  if (typeof value === 'string') {
    return AI_TASK_PRIORITY_NAMES.find((n) => n.toLowerCase() === value.toLowerCase()) ?? 'Normal';
  }
  return 'Normal';
}

/** A queued task awaiting human review, matching AI.Model AITaskQueue. */
export interface HumanReviewTask {
  id: string;
  cardId: string;
  userId: string;
  taskTitle: string;
  taskDescription: string;
  businessContext: string;
  status: AITaskStatus | number;
  priority: AITaskPriority | number;
  queuedAt: string;
  processingStartedAt?: string | null;
  completedAt?: string | null;
  generatedContent?: string | null;
  requiresHumanReview: boolean;
  humanReviewReason?: string | null;
  queuePosition?: number | null;
  estimatedTimeRemaining?: number | null;
  retryCount: number;
  errorMessage?: string | null;
}

export interface HumanReviewStatistics {
  totalAwaitingReview: number;
  criticalPriority: number;
  highPriority: number;
  normalPriority: number;
  lowPriority: number;
  overdueTasks: number;
  averageWaitTimeHours: number;
  completionRate: number;
}

export interface HumanReviewResult<T> {
  success: boolean;
  data?: T;
  /** True when the server returned 403 — caller is not an admin. */
  forbidden?: boolean;
  error?: string;
}

async function call<T>(
  path: string,
  options: RequestInit = {}
): Promise<HumanReviewResult<T>> {
  try {
    // AI.API can be cold after a deploy or idle; the default 10s attempt timeout aborts the
    // request client-side (ERR_ABORTED) long before a cold start answers.
    const response = await authenticatedFetch(
      `${BASE}${path}`,
      {
        ...options,
        headers: { 'Content-Type': 'application/json', ...options.headers },
      },
      { retryConfig: COLDSTART_RETRY_CONFIG },
    );

    if (response.status === 403) {
      return { success: false, forbidden: true, error: 'You need admin access to view the review queue.' };
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return { success: false, error: body.error || body.message || `HTTP ${response.status}` };
    }

    // 204 or empty bodies are possible on some verbs; guard the parse.
    const text = await response.text();
    const data = text ? (JSON.parse(text) as T) : (undefined as unknown as T);
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

/** Lists tasks awaiting review, optionally filtered to one priority. */
export function getReviewQueue(
  priority?: AITaskPriority,
  pageIndex = 0,
  pageSize = 50
): Promise<HumanReviewResult<HumanReviewTask[]>> {
  const params = new URLSearchParams({ pageIndex: String(pageIndex), pageSize: String(pageSize) });
  if (priority) params.set('priority', String(AI_TASK_PRIORITY_VALUE[priority]));
  return call<HumanReviewTask[]>(`/queue?${params.toString()}`);
}

/** Lists tasks past the 24h SLA. */
export function getOverdueTasks(): Promise<HumanReviewResult<HumanReviewTask[]>> {
  return call<HumanReviewTask[]>('/overdue');
}

/** Queue statistics for the dashboard header. */
export function getReviewStatistics(): Promise<HumanReviewResult<HumanReviewStatistics>> {
  return call<HumanReviewStatistics>('/statistics');
}

/** Completes a queued task with admin-supplied content; notifies the owning user. */
export function completeReviewTask(
  queueId: string,
  completedContent: string
): Promise<HumanReviewResult<HumanReviewTask>> {
  return call<HumanReviewTask>(`/${queueId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ completedContent }),
  });
}

/** Escalates a queued task to a higher priority. */
export function escalateReviewTask(
  queueId: string,
  newPriority: AITaskPriority
): Promise<HumanReviewResult<HumanReviewTask>> {
  return call<HumanReviewTask>(`/${queueId}/escalate`, {
    method: 'POST',
    body: JSON.stringify({ newPriority: AI_TASK_PRIORITY_VALUE[newPriority] }),
  });
}
